/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

export interface Option {
  value: number;
  name: string;
  selected?: boolean;
}

interface UsePriceOptionsProps {
  initialMinPriceOptions: Option[];
  initialMaxPriceOptions: Option[];
  formMethods: UseFormReturn<any>;
}

export const usePriceOptions = ({
  initialMinPriceOptions,
  initialMaxPriceOptions,
  formMethods,
}: UsePriceOptionsProps) => {
  const [minPriceOptions, setMinPriceOptions] = useState<Option[]>(initialMinPriceOptions);
  const [maxPriceOptions, setMaxPriceOptions] = useState<Option[]>(initialMaxPriceOptions);
  const [minPrice, setMinPrice] = useState<number>(
    initialMinPriceOptions?.find(({ selected }) => selected)?.value ?? 0
  );
  const [maxPrice, setMaxPrice] = useState<number>(
    initialMaxPriceOptions?.find(({ selected }) => selected)?.value ?? 0
  );

  const { setValue, watch } = formMethods;

  const currentPriceLowest = formMethods.getValues('priceLowest');
  const currentPriceHighest = formMethods.getValues('priceHighest');

  // Update minPriceOptions when initialMinPriceOptions changes
  useEffect(() => {
    setMinPriceOptions(initialMinPriceOptions || []);
    // Optionally reset minPrice when options change
    const selectedMin = initialMinPriceOptions?.find(({ selected }) => selected)?.value ?? 0;
    setMinPrice(selectedMin);
    setValue('priceLowest', selectedMin);
  }, [initialMinPriceOptions, setValue]);

  // Update maxPriceOptions when initialMaxPriceOptions changes
  useEffect(() => {
    setMaxPriceOptions(initialMaxPriceOptions || []);
    // Optionally reset maxPrice when options change
    const selectedMax = initialMaxPriceOptions?.find(({ selected }) => selected)?.value ?? 0;
    setMaxPrice(selectedMax);
    setValue('priceHighest', selectedMax);
  }, [initialMaxPriceOptions, setValue]);

  useEffect(() => {
    if (maxPrice !== undefined) {
      setValue('priceHighest', maxPrice);
    }
  }, [maxPriceOptions]);

  useEffect(() => {
    setMaxPriceOptions(initialMaxPriceOptions?.filter(({ value }) => value >= minPrice));
  }, [minPrice]);

  useEffect(() => {
    if (currentPriceLowest !== undefined && currentPriceLowest !== minPrice) {
      setMinPrice(currentPriceLowest);
    }
  }, [currentPriceLowest]);

  useEffect(() => {
    if (minPrice !== undefined) {
      setValue('priceLowest', minPrice);
    }
  }, [minPriceOptions]);

  useEffect(() => {
    setMinPriceOptions(initialMinPriceOptions?.filter(({ value }) => value <= maxPrice));
  }, [maxPrice]);

  useEffect(() => {
    if (currentPriceHighest !== undefined && currentPriceHighest !== maxPrice) {
      setMaxPrice(currentPriceHighest);
    }
  }, [currentPriceHighest, maxPrice]);

  return {
    minPriceOptions,
    maxPriceOptions,
    minPrice,
    maxPrice,
  };
};
